import { ArrowLeft, Clock, Loader2, Sparkles, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { setAgentRentals } from '@/lib/collections/agentRentals';
import type { AgentsResponse } from '@/lib/collections/agents';
import { Address, Time } from '@/lib/db-client';
import { SOL } from '@/lib/constants';
import { formatSol } from '@/utils/format';
import { quoteRentalWindow, RENTAL_WINDOW_OPTIONS, type RentalWindowOption } from '@/utils/agentRentalPricing';

interface AgentRentalDialogProps {
  agent: AgentsResponse;
  agentId: string;
  walletAddress: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the renter picks "Pay per use" — should navigate into chat. */
  onPayPerUse: () => void;
  /** Called after a rental is successfully paid — should navigate into chat. */
  onRented: () => void;
}

type View = 'choose' | 'rent';

/**
 * Hire entry-point dialog offering the two payment models: pay per message
 * (unchanged existing flow) or rent a fixed time window for unlimited chat.
 * Matches BuyCreditsDialog's glassmorphism-on-theme tone + Robinhood-green CTAs.
 */
export const AgentRentalDialog: React.FC<AgentRentalDialogProps> = ({
  agent,
  agentId,
  walletAddress,
  open,
  onOpenChange,
  onPayPerUse,
  onRented,
}) => {
  const [view, setView] = useState<View>('choose');
  const [selected, setSelected] = useState<RentalWindowOption>(RENTAL_WINDOW_OPTIONS[0]);
  const [renting, setRenting] = useState(false);

  useEffect(() => {
    if (open) {
      setView('choose');
      setRenting(false);
    }
  }, [open]);

  const quote = quoteRentalWindow(agent.pricePerCall, agent.creatorSplitPct, selected);

  const handlePayPerUse = () => {
    onOpenChange(false);
    onPayPerUse();
  };

  const handleRent = async () => {
    setRenting(true);
    try {
      const rentalId = crypto.randomUUID();
      const startedAt = Math.floor(Date.now() / 1000);
      const success = await setAgentRentals(rentalId, {
        agentId,
        renterWallet: Address.publicKey(walletAddress),
        creatorWallet: Address.publicKey(agent.creatorWallet),
        creatorAmount: quote.creatorAmount,
        platformAmount: quote.platformAmount,
        amountPaid: quote.totalLamports,
        tokenMint: SOL,
        durationSeconds: quote.durationSeconds,
        windowLabel: quote.windowLabel,
        startedAt,
        expiresAt: startedAt + quote.durationSeconds,
        timestamp: Time.Now,
      });

      if (!success) {
        toast.error('Rental payment was declined or failed. Please try again.');
        return;
      }

      toast.success(`Rented for ${quote.windowLabel} — unlimited chat unlocked!`);
      onOpenChange(false);
      onRented();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not complete rental.');
    } finally {
      setRenting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {view === 'choose' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#00C805]" /> Hire {agent.name}
              </DialogTitle>
              <DialogDescription>Choose how you'd like to pay.</DialogDescription>
            </DialogHeader>

            <div className="space-y-3 my-1">
              <button
                onClick={handlePayPerUse}
                className="w-full text-left rounded-2xl border border-border bg-card hover:border-[#00C805]/50 hover:bg-[#00C805]/5 transition-all duration-300 p-4 flex items-center gap-3 group"
              >
                <div className="w-10 h-10 rounded-xl bg-[#00C805]/15 flex items-center justify-center shrink-0 group-hover:bg-[#00C805]/25 transition-colors">
                  <Zap className="w-5 h-5 text-[#00C805]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">Pay per use</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatSol(agent.pricePerCall)} per message, charged as you chat.
                  </p>
                </div>
              </button>

              <button
                onClick={() => setView('rent')}
                className="w-full text-left rounded-2xl border border-border bg-card hover:border-[#00C805]/50 hover:bg-[#00C805]/5 transition-all duration-300 p-4 flex items-center gap-3 group"
              >
                <div className="w-10 h-10 rounded-xl bg-[#00C805]/15 flex items-center justify-center shrink-0 group-hover:bg-[#00C805]/25 transition-colors">
                  <Clock className="w-5 h-5 text-[#00C805]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">Rent a window</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Unlimited messages for a fixed time — from {formatSol(quoteRentalWindow(agent.pricePerCall, agent.creatorSplitPct, RENTAL_WINDOW_OPTIONS[0]).totalLamports)}/hr.
                  </p>
                </div>
              </button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <button
                  onClick={() => setView('choose')}
                  aria-label="Back"
                  className="p-1 -ml-1 rounded-full hover:bg-secondary transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <Clock className="w-5 h-5 text-[#00C805]" /> Rent {agent.name}
              </DialogTitle>
              <DialogDescription>Pick a window. Unlimited chat until it expires.</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-4 gap-2 my-1">
              {RENTAL_WINDOW_OPTIONS.map((option) => {
                const isSelected = option.label === selected.label;
                return (
                  <button
                    key={option.label}
                    onClick={() => setSelected(option)}
                    className={`rounded-xl py-2.5 text-sm font-bold transition-all duration-200 border ${
                      isSelected
                        ? 'bg-[#00C805] text-white border-[#00C805] shadow-md shadow-[#00C805]/25'
                        : 'bg-secondary/60 text-foreground border-border hover:border-[#00C805]/40'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 text-center">
              <p className="text-2xl font-extrabold tracking-tight">{formatSol(quote.totalLamports)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Unlimited messages for {selected.count} {selected.unit === 'hour'
                  ? selected.count === 1 ? 'hour' : 'hours'
                  : selected.count === 1 ? 'day' : 'days'}.
              </p>
            </div>

            <button
              onClick={handleRent}
              disabled={renting}
              className="w-full rounded-full bg-[#00C805] text-white font-bold py-3 shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {renting && <Loader2 className="w-4 h-4 animate-spin" />}
              {renting ? 'Processing…' : `Rent for ${selected.label} — ${formatSol(quote.totalLamports)}`}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgentRentalDialog;
